import {
  CompanyEntity,
  ContainerClusterEntity,
  ContainerType,
  FreightTypeEntity, getObjectId,
  Quantity, ScadaMode,
  SiteEntity,
  toContainerType, toScadaMode,
  unitOfMeasureTypes
} from "@ots-frontend/ots-data";
import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Grid, Modal, ModalProps, NumberInput, Switch, TextInput} from "@mantine/core";
import {useCallback} from "react";
import {UnitOfMeasureSelect} from "../../select/entity/UnitOfMeasureSelect/UnitOfMeasureSelect";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {useContainerForm} from "./useContainerForm";
import {CompanySelect} from "../../select/entity/CompanySelect/CompanySelect";
import {FreightTypeSelect} from "../../select/entity/FreightTypeSelect/FreightTypeSelect";
import {ContainerTypeSelect} from "../../select/entity/ContainerTypeSelect/ContainerTypeSelect";
import { ContainerClusterSelect } from "../../select/entity/ContainerClusterSelect/ContainerClusterSelect";
import { SiteSelect } from "../../select/entity/SiteSelect/SiteSelect";
import {ScadaModeSelect} from "../../select/entity/ScadaModeSelect/ScadaModeSelect";

export interface ContainerDimensions {
  height: number;
  diameter: number;
  units: string
}

export interface ContainerFormProps {
  options?: ContainerFormOptions;
  defaultValues?: ContainerFormDefaultValues;
  onSubmit?: (values: ContainerFormValues) => void;
  submitting?: boolean;
}

export interface ContainerFormOptions {

}

export interface ContainerFormValues {
  name: string;
  abbr?: string;
  barcode?: string;
  leaseId?: string;
  leaseName?: string;
  type?: ContainerType
  size: Quantity;
  weight?: Partial<Quantity>
  dimensions?: Partial<ContainerDimensions>,
  company: CompanyEntity | string;
  freightType: FreightTypeEntity | string;
  site: SiteEntity | string;
  cluster?: ContainerClusterEntity | string;
  afe?: string
  afePrefix?: string
  afeSuffix?: string
  scadaMode?: ScadaMode
  scadaModeReason?: string
  dispatchIgnoreMaxNonPrimaryVolume?: boolean
  doNotDispatch?: boolean
  doNotUse?: boolean
  active?: boolean
}

export interface ContainerFormDefaultValues extends Partial<Omit<ContainerFormValues, 'size'>> {
  size?: Partial<Quantity>,
}

export interface ContainerFormRawValues {
  name?: string;
  abbr?: string;
  barcode?: string;
  leaseId?: string;
  leaseName?: string;
  afe?: string
  afePrefix?: string
  afeSuffix?: string
  type?: string;
  size?: number;
  sizeUom?: string
  weight?: number
  weightUom?: string
  dimenHeight: number;
  dimenDiameter: number;
  dimenUom: string
  company?: string;
  freightType?: string;
  site?: string;
  cluster?: string;
  scadaMode?: string;
  scadaModeReason?: string;
  dispatchIgnoreMaxNonPrimaryVolume?: boolean
  doNotDispatch?: boolean
  doNotUse?: boolean
  active?: boolean
}

export type ContainerFormType = UseFormReturnType<ContainerFormRawValues, (values: ContainerFormRawValues) => ContainerFormRawValues>

export const ContainerForm = (props: ContainerFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const region = useApp();

  const {t} = useTranslation(["common"]);

  const form = useContainerForm({
    defaultValues: defaultValues,
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {

    if (onSubmit) {
      onSubmit({
        name: values.name!,
        abbr: values.abbr,
        barcode: values.barcode,
        leaseId: values.leaseId,
        leaseName: values.leaseName,
        afe: values.afe,
        afePrefix: values?.afePrefix,
        afeSuffix: values?.afeSuffix,
        type: values?.type ? toContainerType(values.type) : undefined,
        company: values.company!,
        freightType: values.freightType!,
        site: values.site!,
        cluster: values.cluster,
        size: {
          number: values.size!,
          units: values.sizeUom!,
        },
        weight: {
          number: values.weight,
          units: values.weightUom,
        },
        dimensions: {
          height: values.dimenHeight,
          diameter: values.dimenDiameter,
          units: values.dimenUom,
        },
        scadaMode: values?.scadaMode? toScadaMode(values.scadaMode) : undefined,
        scadaModeReason: values?.scadaModeReason,
        dispatchIgnoreMaxNonPrimaryVolume: values.dispatchIgnoreMaxNonPrimaryVolume,
        doNotDispatch: values.doNotDispatch,
        doNotUse: values.doNotUse,
        active: values.active,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <TextInput
            label={t("common.entity.container.form.field.name", "Name")}
            placeholder={t<string>("common.entity.container.form.field.name.placeholder", "Name")}
            withAsterisk
            required
            {...form.getInputProps("name")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label={t("common.entity.container.form.field.abbr", "Abbreviation")}
            placeholder={t<string>("common.entity.container.form.field.abbr.placeholder", "Abbreviation")}
            {...form.getInputProps("abbr")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label={t("common.entity.container.form.field.barcode", "QR Code")}
            placeholder={t<string>("common.entity.container.form.field.barcode.placeholder", "QR Code")}
            {...form.getInputProps("barcode")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label={t("common.entity.container.form.field.leaseid", "Lease ID")}
            placeholder={t<string>("common.entity.container.form.field.leaseid.placeholder", "Lease ID")}
            {...form.getInputProps("leaseId")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label={t("common.entity.container.form.field.leasename", "Lease Name")}
            placeholder={t<string>("common.entity.container.form.field.leasename.placeholder", "Lease Name")}
            {...form.getInputProps("leaseName")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={6}>
          <CompanySelect
            label={t("common.entity.container.form.field.company", "Company")}
            placeholder={t<string>("common.entity.container.form.field.company.placeholder", "Company")}
            selector={{
              regionId: region.regionId
            }}
            withAsterisk
            required
            clearable
            {...form.getInputProps("company")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <SiteSelect
            label={t("common.entity.container.form.field.site", "Site")}
            placeholder={t<string>("common.entity.container.form.field.site.placeholder", "Site")}
            selector={{
              regionId: region.regionId
            }}
            withAsterisk
            required
            clearable
            {...form.getInputProps("site")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <ContainerClusterSelect
            label={t("common.entity.container.form.field.cluster", "Cluster")}
            placeholder={t<string>("common.entity.container.form.field.cluster.placeholder", "Cluster")}
            selector={{
              siteId: getObjectId(form.getTransformedValues().site),
              regionId: region.regionId
            }}
            clearable
            {...form.getInputProps("cluster")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={6}>
          <ContainerTypeSelect
            label={t("common.entity.container.form.field.type", "Type")}
            placeholder={t<string>("common.entity.container.form.field.type.placeholder", "Type")}
            clearable
            {...form.getInputProps("type")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <FreightTypeSelect
            label={t("common.entity.container.form.field.freighttype", "Freight Type")}
            placeholder={t<string>("common.entity.container.form.field.freighttype.placeholder", "Freight Type")}
            selector={{
              regionId: region.regionId
            }}
            withAsterisk
            required
            clearable
            {...form.getInputProps("freightType")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={4}>
          <TextInput
            label={t("common.entity.container.form.field.afeprefix", "AFE Prefix")}
            placeholder={t<string>("common.entity.container.form.field.afeprefix.placeholder", "AFE Prefix")}
            {...form.getInputProps("afePrefix")} />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            label={t("common.entity.container.form.field.afe", "AFE")}
            placeholder={t<string>("common.entity.container.form.field.afe.placeholder", "AFE")}
            {...form.getInputProps("afe")} />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            label={t("common.entity.container.form.field.afesuffix", "AFE Suffix")}
            placeholder={t<string>("common.entity.container.form.field.afesuffix.placeholder", "AFE Suffix")}
            {...form.getInputProps("afeSuffix")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={6}>
          <ScadaModeSelect
            label={t("common.entity.container.form.field.scadamode", "Scada Mode")}
            placeholder={t<string>("common.entity.container.form.field.scadamode", "Scada Mode")}
            {...form.getInputProps("scadaMode")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label={t("common.entity.container.form.field.scadamodereason", "Scada Mode Reason")}
            placeholder={t<string>("common.entity.container.form.field.scadamodereason", "Scada Mode Reason")}
            {...form.getInputProps("scadaModeReason")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={6}>
          <NumberInput
            label={t("common.entity.container.form.field.height", "Height")}
            placeholder={t<string>("common.entity.container.form.field.height", "Height")}
            precision={5}
            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            {...form.getInputProps("dimenHeight")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <NumberInput
            label={t("common.entity.container.form.field.diameter", "Diameter")}
            placeholder={t<string>("common.entity.container.form.field.diameter", "Diameter")}
            precision={5}
            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            {...form.getInputProps("dimenDiameter")} />
        </Grid.Col>
        <Grid.Col span={6}/>
        <Grid.Col span={6}>
          <UnitOfMeasureSelect
            label={t("common.entity.container.form.field.uom", "Units")}
            placeholder={t<string>("common.entity.container.form.field.uom.placeholder", "Units")}
            selector={{
              types: [unitOfMeasureTypes.linear]
            }}
            clearable
            {...form.getInputProps("dimenUom")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={6}>
          <NumberInput
            label={t("common.entity.container.form.field.size", "Size")}
            placeholder={t<string>("common.entity.container.form.field.size", "Size")}
            precision={5}
            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            withAsterisk
            required
            {...form.getInputProps("size")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <UnitOfMeasureSelect
            label={t("common.entity.container.form.field.uom", "Units")}
            placeholder={t<string>("common.entity.container.form.field.uom.placeholder", "Units")}
            clearable
            {...form.getInputProps("sizeUom")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={6}>
          <NumberInput
            label={t("common.entity.container.form.field.weight", "Weight")}
            placeholder={t<string>("common.entity.container.form.field.weight", "Weight")}
            precision={5}
            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            {...form.getInputProps("weight")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <UnitOfMeasureSelect
            label={t("common.entity.container.form.field.uom", "Units")}
            placeholder={t<string>("common.entity.container.form.field.uom.placeholder", "Units")}
            selector={{
              types: [unitOfMeasureTypes.weight]
            }}
            clearable
            {...form.getInputProps("weightUom")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <Switch
            label={t("common.entity.container.form.field.dispatchignoremaxnonprimaryvolume", "Dispatch Ignore Max Non-Primary Volume")}
            placeholder={t<string>("common.entity.container.form.field.dispatchignoremaxnonprimaryvolume", "Dispatch Ignore Max Non-Primary Volume")}
            {...form.getInputProps("dispatchIgnoreMaxNonPrimaryVolume", {type: 'checkbox'})} />
        </Grid.Col>
        <Grid.Col span={12}>
          <Switch
            label={t("common.entity.container.form.field.donotdispatch", "Do Not Dispatch")}
            placeholder={t<string>("common.entity.container.form.field.donotdispatch", "Do Not Dispatch")}
            {...form.getInputProps("doNotDispatch", {type: 'checkbox'})} />
        </Grid.Col>
        <Grid.Col span={12}>
          <Switch
            label={t("common.entity.container.form.field.donotuse", "Do Not Use")}
            placeholder={t<string>("common.entity.container.form.field.donotuse", "Do Not Use")}
            {...form.getInputProps("doNotUse", {type: 'checkbox'})} />
        </Grid.Col>
        <Grid.Col span={12}>
          <Switch
            label={t("common.entity.container.form.field.active", "Active")}
            placeholder={t<string>("common.entity.container.form.field.active", "Active")}
            {...form.getInputProps("active", {type: 'checkbox'})} />
        </Grid.Col>
      </Grid>
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface ContainerFormModalProps extends ContainerFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const ContainerFormModal = (props: ContainerFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <ContainerForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
